import { Paper, Typography, TableContainer, TableHead, TableRow, TableCell, TableBody, Checkbox } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useContext, useMemo } from "react";
import { SessionContext } from "../../../Contexts/SessionContext";
import WarningPopup from "../../../Popups/WarningPopup";
import { getFilteredSurveys } from "../../../TripRateNew/TriprateFilters";
import DropDownViewModel from "../../../Utilities/ViewModels/DropDownViewModel";
import { useTripRateContext } from "../../TripRateContext";
import Line from '../../../Utilities/Line';
import CompactTable from "../../../Utilities/CompactTable";
import { CounterBadge } from '../../../Utilities/CounterBadge';


const dayNames = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday'
];

// Raw code order to display Monday → Sunday when 1=Sun, 2=Mon, ... 7=Sat
const displayOrderRaw = [2, 3, 4, 5, 6, 7, 1];

interface IProps {
    disabled?: boolean;
}

export default function DaysOfWeek({ disabled = false }: IProps) {
    const { sessionState, setSessionField } = useContext(SessionContext);
    const { surveys } = useTripRateContext();
    const rows = sessionState?.primaryFilters?.daysOfWeek ?? [];

    const filteredSurveys = useMemo(() => {
        return getFilteredSurveys(
            surveys,
            { fetchAction: 'GetDaysOfWeekRanges' }
        );
    }, [surveys, sessionState.primaryFilters]);

    const counts: DropDownViewModel[] = useMemo(() => {
        if (!filteredSurveys?.length) return [];

        // dayCount[rawCode] => count, use slots 1..7
        const dayCount = new Array(8).fill(0);

        for (const s of filteredSurveys) {
            const raw = s.dayOfWeekIndex ?? -1; // 1..7 expected; 1 = Sunday
            if (raw >= 1 && raw <= 7) dayCount[raw]++;
        }

        // Render Monday→Sunday but keep ids as raw codes (so filtering stays correct)
        return displayOrderRaw.map((raw, i) => ({
            id: raw, // raw code (1..7)
            label: `${dayNames[i]} (${dayCount[raw]})`,
        }));
    }, [filteredSurveys]);

    const handleChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const id = counts[index].id; // raw code 1..7
        const updated = e.target.checked
            ? [...rows, id]
            : rows.filter((r: number) => r !== id);
        setSessionField('primaryFilters.daysOfWeek', updated);
    };

    // Conflict check using raw codes: Mon–Fri = 2..6, weekend = 7 & 1
    const weekdaysAndWeekendConflict = useMemo(() => {
        if (!counts.length || !rows.length) return false;
        const weekdays = [2, 3, 4, 5, 6];
        const weekend = [7, 1];
        const hasWeekdays = rows.some(r => weekdays.includes(r));
        const hasWeekend = rows.some(r => weekend.includes(r));
        return hasWeekdays && hasWeekend;
    }, [counts, rows]);


    return (
        <Grid container size={12} component={Paper}
            sx={{ position: 'relative' }}>
            <CounterBadge value={3} />
            <Grid
                container
                size={12}
                alignItems="center"
            >
                <Typography
                    textAlign="center"
                    sx={{ width: '100%', fontWeight: 500 }}
                >
                    Week days to include
                    {weekdaysAndWeekendConflict && <WarningPopup
                        title="Good Practice Guidance Advice - Warning on Week days to include"
                        content="Both weekdays and weekend days are currently selected. Please see the TRICS Good Practice Guide."
                        type="error"
                    />}
                </Typography>
            </Grid>


            <Line />
            <Grid size={12}>
                <TableContainer>
                    <CompactTable>
                        <TableHead>
                            <TableRow>
                                <TableCell>Day</TableCell>
                                <TableCell>Count</TableCell>
                                <TableCell>
                                    Include{' '}

                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {counts?.map((x, index) => {
                                const [dayName, count] = x.label.split('(');
                                const countValue = Number(count?.replace(')', '') ?? 0);
                                const isChecked = rows.includes(x.id);

                                return (
                                    <TableRow key={x.id}>
                                        <TableCell>{dayName.trim()}</TableCell>
                                        <TableCell>{countValue}</TableCell>
                                        <TableCell>
                                            <Checkbox
                                                checked={isChecked}
                                                onChange={handleChange(index)}
                                                disabled={disabled}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </CompactTable>
                </TableContainer>
            </Grid>
        </Grid>
    );
}
